// Single source of truth for deriving "special of" VISIBILITY shadowing at
// read time. Imported by the server (procedureResolver.ts § procedures — the
// anonymous public catalog wire) and the client funnel (QuoteProvider catalog
// filter, admin EstimatorModal) via the `@shared` alias.
//
// SCOPE — VISIBILITY, PER LIST. A procedure can carry `specialOfProcedureId`,
// marking it a marketing special of another procedure. Whenever the SPECIAL is
// publicly visible within a given already-filtered display list, the procedure
// it references is dropped from that SAME list. This is derived, not stored —
// run it as the LAST step of each visibility pipeline, after isInternal /
// activeWindow / gender / etc. have already narrowed the list down to what
// would otherwise be shown. Pricing is unaffected — see
// server/shared/pricing/activeWindow.ts for the sibling visibility window.

import { isWithinActiveWindow } from "./activeWindow";

// A raw id can arrive as a plain string, a Mongo ObjectId (via `toString()`),
// or absent. Coerce to a comparable string; return null for absent/garbage.
const OBJECT_ID_HEX_RE = /^[0-9a-fA-F]{24}$/;

function toIdString(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "string") return value.length > 0 ? value : null;
  if (typeof value === "object" || typeof value === "number") {
    // Non-string values only count if they stringify to a real Mongo
    // ObjectId hex shape (e.g. a genuine ObjectId instance) — anything else
    // (a plain `{}`, a stray number, ...) stringifies to noise like
    // "[object Object]" or "42" and is treated as no id, per the module
    // comment's fail-open contract.
    const s = String(value);
    return OBJECT_ID_HEX_RE.test(s) ? s : null;
  }
  return null;
}

export interface SpecialShadowInput {
  id?: unknown;
  _id?: unknown;
  specialOfProcedureId?: unknown;
}

function memberId(member: SpecialShadowInput): string | null {
  return toIdString(member.id) ?? toIdString(member._id);
}

/**
 * Given an already-filtered display list, drop any member whose id is
 * referenced (via `specialOfProcedureId`) by another member of that SAME
 * list — i.e. hide the base procedure a live special is shadowing.
 *
 *   - A member with no/empty `specialOfProcedureId` neither shadows anything
 *     nor is itself dropped by this pass (it may still be dropped if some
 *     OTHER member shadows it).
 *   - A self-reference (`specialOfProcedureId === own id`) is ignored when
 *     building the shadow set — it can never hide the member itself.
 *   - Multiple specials referencing the same base procedure simply union
 *     into the same shadow set — the base is dropped once.
 *   - The shadowing special is never dropped by its own reference; it is
 *     only dropped if some OTHER member of the list shadows it in turn.
 *   - Fails open: any garbage/unexpected id shape is treated as "no id" and
 *     never throws.
 */
export function filterSpecialShadowed<T extends SpecialShadowInput>(
  list: readonly T[],
): T[] {
  if (!list || list.length === 0) return [];

  const shadowed = new Set<string>();
  for (const member of list) {
    const specialOf = toIdString(member.specialOfProcedureId);
    if (!specialOf) continue;
    const ownId = memberId(member);
    if (ownId != null && specialOf === ownId) continue; // ignore self-reference
    shadowed.add(specialOf);
  }

  if (shadowed.size === 0) return list.slice();

  return list.filter((member) => {
    const ownId = memberId(member);
    return ownId == null || !shadowed.has(ownId);
  });
}

export interface PublicCatalogSpecialShadowInput extends SpecialShadowInput {
  isInternal?: boolean | null;
  isDiscontinued?: boolean | null;
  activeStartsAt?: Date | number | string | null;
  activeEndsAt?: Date | number | string | null;
}

/**
 * Given the FULL catalog — unlike `filterSpecialShadowed` above, this may
 * still include window-hidden members; callers (the funnel, admin surfaces)
 * window-filter themselves — drop any member whose id is shadowed by a
 * PUBLICLY LIVE special elsewhere in the same list.
 *
 *   - A member only shadows its `specialOfProcedureId` target if the member
 *     ITSELF is publicly live right now: not `isInternal`, not
 *     `isDiscontinued`, and within its active window (`isWithinActiveWindow`).
 *     A window-expired, internal, or discontinued special never shadows.
 *   - The output is NOT window-filtered — members are only ever removed here
 *     because they are shadowed, never because they're outside their own
 *     window. Every non-shadowed member of the input is returned unchanged.
 *   - A self-reference (`specialOfProcedureId === own id`) is ignored when
 *     building the shadow set, same as `filterSpecialShadowed`.
 *   - Fails open: any garbage/unexpected id or window shape is treated as
 *     "no id" / "live" and never throws.
 */
export function filterSpecialShadowedForPublicCatalog<
  T extends PublicCatalogSpecialShadowInput,
>(list: readonly T[]): T[] {
  if (!list || list.length === 0) return [];

  const shadowed = new Set<string>();
  for (const member of list) {
    if (member.isInternal || member.isDiscontinued) continue;
    if (!isWithinActiveWindow(member)) continue;
    const specialOf = toIdString(member.specialOfProcedureId);
    if (!specialOf) continue;
    const ownId = memberId(member);
    if (ownId != null && specialOf === ownId) continue; // ignore self-reference
    shadowed.add(specialOf);
  }

  if (shadowed.size === 0) return list.slice();

  return list.filter((member) => {
    const ownId = memberId(member);
    return ownId == null || !shadowed.has(ownId);
  });
}
